import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, type PropsWithChildren } from 'react';

import { buildColorRule } from '../../core/color-rule/builder.js';
import { usePieChartConfig } from '../context/PieChartConfigContext.js';
import { usePieChartRawData } from '../context/PieChartRawDataContext.js';
import { PieChartSlicesDataContext } from '../context/PieChartSlicesData.js';
import { defaultGrouping } from '../defaults.js';
import { useSetHighlightedIdState } from '../hooks/store/use-set-highlighted-ids-state.js';
import { useSetVisibleSlicesState } from '../hooks/store/use-set-visible-slices-state.js';
import type { InternalSliceData } from '../types/internal/pie-chart.js';
import { buildGroupThreshold } from '../utils/build-group-threshold.js';

export const PieChartSlicesDataProvider = ({ children }: PropsWithChildren) => {
  const chartData = usePieChartRawData();
  const chartConfig = usePieChartConfig();

  const setHighlightedIdState = useSetHighlightedIdState();
  const setVisibleSlicesState = useSetVisibleSlicesState();

  const isDataAvailable = !isEmpty(chartData);

  const groupingKey = useMemo(
    () => JSON.stringify(chartConfig.grouping ?? defaultGrouping),
    [chartConfig.grouping],
  );

  const reRenderDependencies = [
    chartConfig.colorPalette,
    groupingKey,
    chartData?.slices,
    chartData?.unit,
    isDataAvailable,
  ];

  const slices = useMemo(
    () =>
      isDataAvailable
        ? buildGroupThreshold(
            chartData?.slices,
            chartConfig.grouping ?? defaultGrouping,
            chartConfig.colorPalette,
            chartData?.unit,
          )
        : [],
    reRenderDependencies,
  );

  const { slicesWithColorRule } = useMemo(() => {
    const colorRule = buildColorRule(chartConfig.colorRules || [])(
      slices,
      'pieOrDonut',
    );

    return slices.reduce(
      (acc, slice) => {
        const colorFromRule = colorRule.for(slice);

        acc.slicesWithColorRule.push({
          ...slice,
          color: slice._priorityColor ?? colorFromRule ?? slice.color,
        });
        return acc;
      },
      {
        slicesWithColorRule: [] as InternalSliceData[],
      },
    );
  }, reRenderDependencies);

  useEffect(() => {
    setHighlightedIdState(undefined);
    setVisibleSlicesState(slicesWithColorRule);
  }, reRenderDependencies);

  const ctxValue = useMemo(
    () => ({
      slices: slicesWithColorRule,
    }),
    [slicesWithColorRule],
  );
  return (
    <PieChartSlicesDataContext.Provider value={ctxValue}>
      {children}
    </PieChartSlicesDataContext.Provider>
  );
};
